// HeySyncly api
import { v1ApiClient } from '@/services';
import type { GptSocketBasePayload } from '@/types/gptConst';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import type {
  ExportMentionListRequestParams,
  HEY_SYNCLY_SOURCE_TYPE,
  HeySynclyChat,
  HeySynclyChatAskRequestParams,
  HeySynclyChatVoteRequestParams,
  HeySynclyHistory,
} from '../model';
import { retrieveHeySynclyChatFeedbacks } from './retrieve-hey-syncly-chat-feedbacks';

export const heySynclyService = {
  AskHeySynclyChat: async (
    params: HeySynclyChatAskRequestParams,
  ): Promise<HeySynclyChat> => {
    const workspaceAlias = getCurrWorkspaceAlias();
    const filter = params.filter ?? {};
    const requestBody = {
      question: params.question,
      socket_id: params.socket_id,
      filter_string: params.filter_string,
      filter,
      ...(params.chat_alias ? { chat_alias: params.chat_alias } : {}),
      ...(params.item_type ? { item_type: params.item_type } : {}),
      ...(params.prompt_library_id
        ? { prompt_library_id: params.prompt_library_id }
        : {}),
    };
    const response = await v1ApiClient.post(
      `/workspaces/${workspaceAlias}/hey-syncly/chats/ask`,
      requestBody,
    );
    return response.data;
  },
  async AskHeySynclyFromOtherSource(
    socketBase: GptSocketBasePayload,
    filter: StructuredFilter,
    source: HEY_SYNCLY_SOURCE_TYPE,
  ): Promise<HeySynclyChat> {
    const wa = getCurrWorkspaceAlias();
    const res = await v1ApiClient.post(`/workspaces/${wa}/hey-syncly/ask`, {
      ...socketBase,
      source,
      filter: filter,
    });
    return res.data;
  },
  GetHeySynclyChat: async (chat_alias: Alias): Promise<HeySynclyChat> => {
    const response = await v1ApiClient.get(`/hey-syncly/chats/${chat_alias}`);
    return response.data;
  },
  RetrieveHeySynclyChats: async (
    offset: number = 0,
    limit: number = 20,
  ): Promise<HeySynclyHistory[]> => {
    const workspaceAlias = getCurrWorkspaceAlias();
    const response = await v1ApiClient.get(
      `/workspaces/${workspaceAlias}/hey-syncly/chats`,
      {
        params: {
          offset,
          limit,
        },
      },
    );
    return response.data;
  },

  DeleteHeySynclyChat: async (chat_alias: Alias): Promise<void> => {
    await v1ApiClient.delete(`/hey-syncly/chats/${chat_alias}`);
  },

  VoteHeySynclyChat: async (
    params: HeySynclyChatVoteRequestParams,
  ): Promise<void> => {
    const { chat_alias, message_id, vote } = params;
    await v1ApiClient.post(
      `/hey-syncly/chats/${chat_alias}/${message_id}/votes`,
      {
        vote_up: vote,
      },
    );
  },

  RetrieveHeySynclyChatFeedbacks: retrieveHeySynclyChatFeedbacks,

  ExportMentionList: async (
    params: ExportMentionListRequestParams,
  ): Promise<void> => {
    const { chat_alias, message_id, citation_ids, reason, password } = params;
    const response = await v1ApiClient.post(
      `/hey-syncly/chats/${chat_alias}/feedbacks/export`,
      {
        chat_alias,
        message_id,
        citation_ids,
        reason,
        password,
      },
    );
    return response.data;
  },
};
